import { environment } from '../../../environments/environment';

/** Alinhado ao backend: Lucide kebab-case, máx. 64. */
const LUCIDE_ICON_KEY_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

const LEGACY_SEMANTIC_TO_LUCIDE: Record<string, string> = {
  workflow: 'columns-3',
  onboarding: 'sparkles',
  support: 'headset',
  users: 'users',
  briefcase: 'briefcase',
  clipboard: 'clipboard-list',
  wrench: 'wrench',
  file: 'file-text',
  'check-square': 'check-square',
  layers: 'layers',
};

const LEGACY_SEMANTIC_KEYS = new Set(Object.keys(LEGACY_SEMANTIC_TO_LUCIDE));

/** Indica se o valor é URL absoluta ou path de asset (não é id Lucide). */
export function isWorkflowIconAssetKey(key: string | null | undefined): boolean {
  const t = (key ?? '').trim();
  if (!t) return false;
  if (/^https?:\/\//i.test(t)) return true;
  return t.startsWith('/');
}

/**
 * URL absoluta para <img src> (path relativo junta à base da API).
 */
export function resolveWorkflowIconAssetUrl(key: string | null | undefined): string | null {
  if (!isWorkflowIconAssetKey(key)) return null;
  const raw = (key ?? '').trim();
  if (/^https?:\/\//i.test(raw) || raw.startsWith('data:')) return raw;
  const normalized = raw.replace(/\\/g, '/').replace(/\/{2,}/g, '/');
  const path = normalized.startsWith('/') ? normalized : `/${normalized}`;
  const baseUrl = environment.apiUrl.replace(/\/$/, '');
  return `${baseUrl}${path}`;
}

/**
 * Normaliza o valor vindo da API para o estado interno da UI (picker + Lucide).
 * — Preserva assets (path / ou URL) até 500 chars.
 * — Mapeia chaves semânticas legadas para um id Lucide equivalente.
 * — Valida id Lucide (regex + length) ou usa fallback.
 */
export function normalizeWorkflowIconKeyForUi(raw: string | null | undefined): string {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return 'columns-3';

  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('/')) {
    return trimmed.length > 500 ? trimmed.slice(0, 500) : trimmed;
  }

  const lower = trimmed.toLowerCase();
  if (LEGACY_SEMANTIC_TO_LUCIDE[lower]) {
    return LEGACY_SEMANTIC_TO_LUCIDE[lower];
  }

  const normalized = lower.replace(/[_\s]+/g, '-').replace(/[^a-z0-9-]/g, '');
  if (
    normalized.length > 0 &&
    normalized.length <= 64 &&
    LUCIDE_ICON_KEY_PATTERN.test(normalized)
  ) {
    return normalized;
  }

  return 'columns-3';
}

/**
 * Valor a enviar no PUT/POST de workflow: Lucide em minúsculas, semântica legada, ou asset.
 */
export function workflowIconKeyToApi(ui: string | null | undefined): string {
  const t = (ui ?? '').trim();
  if (!t) return 'columns-3';

  if (/^https?:\/\//i.test(t) || t.startsWith('/')) {
    return t.length > 500 ? t.slice(0, 500) : t;
  }

  const lower = t.toLowerCase();
  const normalized = lower.replace(/[_\s]+/g, '-').replace(/[^a-z0-9-]/g, '');

  if (LEGACY_SEMANTIC_KEYS.has(normalized)) {
    return normalized;
  }

  if (
    normalized.length > 0 &&
    normalized.length <= 64 &&
    LUCIDE_ICON_KEY_PATTERN.test(normalized)
  ) {
    return normalized;
  }

  return 'columns-3';
}

/** Extrai path/URL do body do POST .../workflows/{id}/icon (contratos flexíveis). */
export function readWorkflowIconUploadResponse(body: unknown): string | null {
  if (body == null) return null;
  if (typeof body === 'string') {
    const s = body.trim();
    return s || null;
  }
  if (typeof body !== 'object') return null;
  const r = body as Record<string, unknown>;
  const keys = ['iconKey', 'assetUrl', 'url', 'path', 'iconUrl', 'publicUrl'] as const;
  for (const k of keys) {
    const v = r[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  const data = r['data'];
  if (data && typeof data === 'object') {
    return readWorkflowIconUploadResponse(data);
  }
  return null;
}
